import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Popup } from 'devextreme-react/popup'
import Button from 'devextreme-react/button'
import FileUploader from 'devextreme-react/file-uploader'
import RadioGroup, { RadioGroupTypes } from 'devextreme-react/radio-group'
import axios from 'axios'
import { useLocation } from 'react-router-dom'
import { socketGet } from '../../socket'
import UploadProgressBar from './UploadProgressBar'
import notify from '../../utils/notify'
import * as XLSX from 'xlsx'
import JSZip from 'jszip'
import mime from 'mime'
import DateBox from 'devextreme-react/date-box'
import SelectBox from 'devextreme-react/select-box'
import CheckBox from 'devextreme-react/check-box'
import TextBox from 'devextreme-react/text-box'

export default function UploadDialog(props: any) {
	const { onHide, onComplete, onErrorFields, onAbort } = props
	const formElement = useRef<any | null>(null)
	const [selectedFile, setSelectedFile] = useState<File | Blob | null>(null)
	const [requiredFields, setRequiredFields] = useState<any[]>([])
	const [keyFields, setKeyFields] = useState<any[]>([])
	const location = useLocation()
	const searchParams = new URLSearchParams(location.search)
	const process = searchParams.get('process')
	const socket = socketGet()
	const [loading, setLoading] = useState(false)
	const [progressData, setProgressData] = useState<
		Record<string, { percent: number; progressMessage: string; filename: string }>
	>({})
	const [progress, setProgress] = useState<any | null>(null)
	const [errorFields, setErrorFields] = useState<any[]>([])
	const [extraParams, setExtraParams] = useState<any[]>([])
	const [allowMultiUpload, setAllowMultiUpload] = useState(false)
	const [allowFolderUpload, setAllowFolderUpload] = useState(false)
	const [allowExtensions, setAllowExtensions] = useState<string[]>([])
	const [isFileTypeAllowed, setIsFileTypeAllowed] = useState(true)
	const [uploadType, setUploadType] = useState('File')
	const [countFiles, setCountFiles] = useState(-1)
	const [allFiles, setAllFiles] = useState(0)
	const [selectedNames, setSelectedNames] = useState<string[]>([])
	// Files this dialog is tracking. The socket server also sends progress of the user's imports
	// started in other browsers/tabs (other processes too), so anything else is ignored.
	const ownFiles = useRef<Set<string>>(new Set())

	const isOwnEvent = useCallback(
		(data: any) => {
			if (data.process && data.process !== process) {
				return false
			}
			return !data.filename || ownFiles.current.has(data.filename)
		},
		[process]
	)

	// Validation function to check if all required parameters are filled
	const validateRequiredParams = useCallback(() => {
		return extraParams.every(param => {
			if (!param.required) return true
			if (param.type === 'checkbox') {
				return param.value !== undefined && param.value !== null
			}
			return param.value !== undefined && param.value !== null && param.value !== ''
		})
	}, [extraParams])

	const changeUploadType = useCallback(
		(e: RadioGroupTypes.ValueChangedEvent) => {
			setUploadType(e.value)
		},
		[setUploadType]
	)

	useEffect(() => {
		if (countFiles === 0) {
			setLoading(false)
			onComplete()
			setCountFiles(-1)
			setAllFiles(0)
			setProgressData({})
		}
	}, [onComplete, countFiles])

	useEffect(() => {
		if (progress && progress.files > 0) {
			setCountFiles(progress.files)
			if (progress.filename) {
				setProgressData(prev => ({
					...prev,
					[progress.filename]: {
						percent: progress.percent,
						progressMessage: progress.message || '',
						filename: progress.filename,
					},
				}))
			}
			setAllFiles(progress.all_files)
			setLoading(true)
		}
	}, [progress])

	useEffect(() => {
		const handlerProgress = (data: any) => {
			const { percent, message, filename, files } = data
			if (!isOwnEvent(data)) {
				return
			}
			if (filename) {
				if (files) {
					setCountFiles(files)
				}
				if (percent === -1 || percent === undefined) {
					return
				}
				setProgressData(prev => {
					const currentData = prev[filename] || { percent: 0, progressMessage: '', filename: filename }
					if (percent === 100) {
						if (countFiles === 1) {
							const updated = {
								...prev,
								[filename]: {
									...currentData,
									percent: 100,
									progressMessage: message || currentData.progressMessage,
								},
							}
							setTimeout(() => {
								setCountFiles(0)
								setProgressData(prevData => {
									const { [filename]: _, ...rest } = prevData
									return rest
								})
							}, 5000)
							return updated
						} else {
							setCountFiles(countFiles - 1)
							// Keep the completed file in progressData with 100% until all files are done
							return {
								...prev,
								[filename]: {
									...currentData,
									percent: 100,
									progressMessage: message || currentData.progressMessage,
									filename: filename,
								},
							}
						}
					} else {
						const updatedData = {
							...prev,
							[filename]: {
								percent: percent,
								progressMessage: message || currentData.progressMessage,
								filename: filename,
							},
						}
						console.log('progressData updated:', updatedData, 'filename:', filename, 'percent:', percent)
						return updatedData
					}
				})
			} else if (files) {
				setCountFiles(files)
			}
		}
		const handlerAbort = (data: any) => {
			if (!isOwnEvent(data)) {
				return
			}
			if (data.message) {
				notify(data.message, 'error')
				setLoading(false)
				setCountFiles(-1)
				setAllFiles(0)
				setProgressData({})
				onAbort?.()
			} else {
				setTimeout(() => {
					setCountFiles(countFiles - 1)
					if (data.filename) {
						setProgressData(prev => {
							const { [data.filename]: _, ...rest } = prev
							return rest
						})
					}
				}, 1000)
			}
		}
		const handlerError = (data: any) => {
			if (!isOwnEvent(data)) {
				return
			}
			if (data.fields) {
				setErrorFields(data.fields.toString().split(': ')[1].split(','))
				onErrorFields()
				setLoading(false)
				setCountFiles(-1)
				setAllFiles(0)
				setProgressData({})
			}
		}

		if (socket) {
			socket.on('legacy_import_progress', handlerProgress)
			socket.on('legacy_import_abort', handlerAbort)
			socket.on('legacy_import_error', handlerError)

			return () => {
				socket.off('legacy_import_progress', handlerProgress)
				socket.off('legacy_import_abort', handlerAbort)
				socket.off('legacy_import_error', handlerError)
			}
		}
	}, [onAbort, onErrorFields, socket, process, countFiles, isOwnEvent])

	useEffect(() => {
		axios
			.get(`${API_FIBER_URL}/imports/fields?process=${process}`)
			.then(response => {
				setRequiredFields(response.data.fields)
				setKeyFields(response.data.keyFields)
				setAllowMultiUpload(response.data.allowMultiUpload)
				setAllowFolderUpload(response.data.allowFolderUpload)
				setAllowExtensions(response.data.allowExtensions || [])
				setExtraParams(response.data.extraParams || [])
				const progress = response.data.progress
				ownFiles.current = new Set(
					[...(progress?.filenames || []), progress?.filename].filter((name: string) => !!name)
				)
				setProgress(progress)
			})
			.catch(error => {
				console.error(error)
			})
	}, [process])

	const handleFileSelection = (e: any) => {
		let cnt = 0
		const names: string[] = []
		const zip = new JSZip()
		if (e.value.length > 1) {
			e.value.forEach((file: File) => {
				if (file.type && file.name[0] !== '.' && file.name[0] !== '~') {
					zip.file(file.name, file)
					names.push(file.name)
					cnt++
				}
			})
		} else if (e.value.length === 1) {
			cnt = 1
			const file = e.value[0]
			zip.file(file.name, file)
			names.push(file.name)

			if (file && allowExtensions.length > 0) {
				const fileExtension = file.name.split('.').pop()?.toLowerCase()
				setIsFileTypeAllowed(fileExtension ? allowExtensions.includes(`.${fileExtension}`) : false)
			} else {
				setIsFileTypeAllowed(true)
			}
		}
		zip.generateAsync({
			type: 'blob',
			compression: 'DEFLATE',
			compressionOptions: {
				level: 9,
			},
		}).then(function (blob) {
			setSelectedFile(blob)
			setSelectedNames(names)
			setCountFiles(cnt)
			setAllFiles(cnt)
		})
	}

	const ListItemField = (props: any) => {
		const { field } = props
		if (errorFields && errorFields.indexOf(field) > -1) {
			return <span style={{ color: 'red' }}>{field}</span>
		}
		return <span>{field}</span>
	}

	const handleUpload = () => {
		if (selectedFile) {
			const formData = new FormData()
			formData.append('process', process as string)
			formData.append('file', selectedFile)
			extraParams.forEach(param => {
				formData.append('extra[' + param.name + ']', param.value)
			})
			ownFiles.current = new Set(selectedNames)
			setLoading(true)
			axios
				.post(`${API_FIBER_URL}/imports`, formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				})
				.then(response => {
					if (!response.data.success) {
						notify('Incorrect filename', 'error')
						setLoading(false)
					}
				})
				.catch(error => {
					console.error('Error uploading data:', error)
					setLoading(false)
				})
		}
	}

	const handleDownloadClick = () => {
		const tsvRows = [requiredFields.join('\t')].join('\n')
		const workbook = XLSX.utils.book_new()
		const worksheet = XLSX.utils.aoa_to_sheet(tsvRows.split('\n').map(row => row.split('\t')))
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')
		const wbout = XLSX.write(workbook, { bookType: 'xlsx', bookSST: false, type: 'array' })
		const xlsxBlob = new Blob([wbout], { type: 'application/octet-stream' })
		const downloadLink = document.createElement('a')
		downloadLink.href = URL.createObjectURL(xlsxBlob)
		const fileName = `${process}.xlsx`
		downloadLink.setAttribute('download', fileName)
		document.body.appendChild(downloadLink)
		downloadLink.click()
		document.body.removeChild(downloadLink)
	}

	const acceptTypesByExtention = (extensions: string[]) => {
		return extensions.map(ext => (mime as any).getType(ext)).join(', ')
	}

	const uploadFolder = uploadType === 'Folder'

	const renderContent = () => {
		return (
			<>
				{loading &&
					Object.values(progressData).map((fileData, index) => (
						<UploadProgressBar
							key={fileData.filename || index}
							percent={fileData.percent}
							progressMessage={fileData.progressMessage}
							filename={allFiles > 1 ? fileData.filename : ''}
						/>
					))}
				<p style={{ fontSize: '20px', textAlign: 'center' }}>Import from excel</p>
				<form
					id="form"
					ref={formElement}
					method="post"
					action=""
					style={{ border: '1px solid lightgrey', borderRadius: '7px' }}
				>
					<div>
						<div style={{ width: '55%', float: 'left' }}>
							<FileUploader
								selectButtonText="CLICK TO SELECT"
								labelText=""
								uploadMode="useForm"
								onValueChanged={handleFileSelection}
								inputAttr={{ webkitdirectory: uploadFolder }}
								multiple={uploadFolder || allowMultiUpload}
								disabled={loading}
								{...(allowExtensions.length > 0 && {
									allowedFileExtensions: allowExtensions,
									accept: acceptTypesByExtention(allowExtensions),
								})}
							/>
						</div>
						{allowFolderUpload && (
							<div style={{ float: 'left', marginTop: '15px' }}>
								<RadioGroup
									items={['File', 'Folder']}
									value={uploadType}
									layout="horizontal"
									onValueChanged={changeUploadType}
									disabled={loading}
								/>
							</div>
						)}
					</div>
					{extraParams.length > 0 && (
						<div style={{ clear: 'both', padding: '10px' }}>
							{extraParams.map(param => {
								const isRequired = param.required
								const isFieldValid =
									!isRequired ||
									(param.value !== undefined && param.value !== null && param.value !== '')
								const fieldStyle = isRequired && !isFieldValid ? { border: '1px solid red' } : {}

								return (
									<div key={param.name} style={{ marginBottom: '10px' }}>
										{param.type === 'select' && (
											<SelectBox
												label={isRequired ? `${param.title} *` : param.title}
												items={param.options}
												value={param.value}
												onValueChanged={(e: any) => {
													setExtraParams(prev =>
														prev.map(p =>
															p.name === param.name ? { ...p, value: e.value } : p
														)
													)
												}}
												displayExpr="name"
												valueExpr="key"
												elementAttr={fieldStyle}
											/>
										)}
										{param.type === 'checkbox' && (
											<CheckBox
												text={isRequired ? `${param.title} *` : param.title}
												hint={isRequired ? `${param.title} *` : param.title}
												value={param.value}
												enableThreeStateBehavior={false}
												onValueChanged={(e: any) => {
													setExtraParams(prev =>
														prev.map(p =>
															p.name === param.name ? { ...p, value: e.value } : p
														)
													)
												}}
												elementAttr={fieldStyle}
											/>
										)}
										{param.type === 'text' && (
											<TextBox
												label={isRequired ? `${param.title} *` : param.title}
												value={param.value}
												onValueChanged={(e: any) => {
													setExtraParams(prev =>
														prev.map(p =>
															p.name === param.name ? { ...p, value: e.value } : p
														)
													)
												}}
												elementAttr={fieldStyle}
											/>
										)}
										{param.type === 'date' && (
											<DateBox
												label={isRequired ? `${param.title} *` : param.title}
												value={param.value}
												onValueChanged={(e: any) => {
													setExtraParams(prev =>
														prev.map(p =>
															p.name === param.name ? { ...p, value: e.value } : p
														)
													)
												}}
												elementAttr={fieldStyle}
											/>
										)}
									</div>
								)
							})}
						</div>
					)}
					<Button
						text="Upload"
						onClick={handleUpload}
						disabled={loading || !selectedFile || !isFileTypeAllowed || !validateRequiredParams()}
						width={'30dvw'}
					/>
				</form>
				{requiredFields.length > 0 && (
					<>
						<br />
						<Button text="Download Excel Template" onClick={handleDownloadClick} icon="download" />
						<br />
						<p>
							<strong>Required Fields:</strong>
						</p>
						<ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
							{requiredFields.map((item, index) => (
								<li key={index}>
									{(Array.isArray(keyFields) && keyFields.includes(item)) || keyFields === item ? (
										<>
											<ListItemField field={item} /> (*)
										</>
									) : (
										<ListItemField field={item} />
									)}
								</li>
							))}
						</ul>
					</>
				)}
			</>
		)
	}

	return (
		<Popup
			visible={true}
			showTitle={false}
			contentRender={renderContent}
			position="center"
			width={'30dvw'}
			minWidth={'400px'}
			height={'80dvh'}
			hideOnOutsideClick={() => {
				return true
			}}
			onHiding={onHide}
		/>
	)
}
